import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCatalog, getManifest } from '../api';
import type { Catalog } from '../types';
import { Upload, Package, RefreshCw } from 'react-feather';

export const Home: React.FC = () => {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalMediaCount, setTotalMediaCount] = useState(0);

  useEffect(() => {
    loadCatalog();
  }, []);

  const loadCatalog = async () => {
    try {
      setLoading(true);
      const data = await getCatalog();
      setCatalog(data);
      setError(null);

      // Fetch all manifests to count total media
      await loadTotalMediaCount(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load catalog');
    } finally {
      setLoading(false);
    }
  };

  const loadTotalMediaCount = async (catalog: Catalog) => {
    try {
      let totalCount = 0;

      // Fetch each manifest and count items
      for (const pack of catalog.packs) {
        try {
          // Extract version from versionPath
          const versionMatch = pack.versionPath.match(/\/v(\d+)\//);
          const version = versionMatch && versionMatch[1] ? parseInt(versionMatch[1]) : 1;

          // Use API to fetch manifest (handles CORS)
          const manifest = await getManifest(pack.id, version);
          totalCount += manifest.items?.length || 0;
        } catch (err) {
          console.error(`Failed to load manifest for ${pack.id}:`, err);
        }
      }

      setTotalMediaCount(totalCount);
    } catch (err) {
      console.error('Failed to count total media:', err);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="header">
          <h2>Dashboard</h2>
        </div>
        <div className="card">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div className="spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="header">
          <h2>Dashboard</h2>
        </div>
        <div className="alert alert-error">{error}</div>
        <button className="btn btn-primary" onClick={loadCatalog}>
          Retry
        </button>
      </div>
    );
  }

  // Get unique categories count
  const uniqueCategories = new Set(catalog?.packs.map(p => p.category) || []);
  const categoryCount = uniqueCategories.size;

  return (
    <div className="container">
      <div className="header">
        <h2>Dashboard</h2>
        <p>Manage your content categories and media</p>
      </div>

      {/* Summary Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px',
        marginBottom: '30px'
      }}>
        <div className="card" style={{ textAlign: 'center', padding: '30px 20px' }}>
          <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '5px' }}>
            {categoryCount}
          </div>
          <div style={{ color: '#666' }}>Categories</div>
        </div>

        <div className="card" style={{ textAlign: 'center', padding: '30px 20px' }}>
          <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '5px' }}>
            {totalMediaCount}
          </div>
          <div style={{ color: '#666' }}>Media</div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card" style={{ marginBottom: '30px' }}>
        <h3 style={{ marginBottom: '20px' }}>Quick Actions</h3>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/upload')}
            style={{ fontSize: '16px', padding: '12px 24px' }}
          >
            <Upload size={18} />
            Add Media
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/categories')}
            style={{ fontSize: '16px', padding: '12px 24px' }}
          >
            <Package size={18} />
            View Categories
          </button>
          <button
            className="btn btn-secondary"
            onClick={loadCatalog}
            style={{ fontSize: '16px', padding: '12px 24px' }}
          >
            <RefreshCw size={18} />
            Refresh
          </button>
        </div>
      </div>
    </div>
  );
};
